import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Activity,
    RefreshCw,
    CheckCircle2,
    XCircle,
    Database,
    HardDrive,
    Mail,
    Zap,
    Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { healthAPI } from '../../lib/api';

/**
 * Green / red status pill.
 *
 * Renders an icon + label + optional latency badge. Used for each of the
 * four probes surfaced by `/api/health/deep` (DB, R2 write, Resend, Mongo
 * counts). `ok=null` renders as neutral gray for the initial loading state.
 */
const Pill = ({ icon: Icon, label, ok, latencyMs, extra, testid }) => {
    const bg = ok === null
        ? 'bg-gray-100 text-gray-500 border-gray-200'
        : ok
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-red-50 text-red-700 border-red-200';
    const StatusIcon = ok === null
        ? Loader2
        : ok ? CheckCircle2 : XCircle;
    return (
        <div
            className={`flex items-center justify-between px-4 py-3 rounded-lg border ${bg}`}
            data-testid={testid}
        >
            <div className="flex items-center gap-3">
                <Icon size={18} className="shrink-0" />
                <div>
                    <div className="text-sm font-semibold">{label}</div>
                    {extra && (
                        <div className="text-[11px] opacity-80 font-mono mt-0.5">
                            {extra}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2">
                {typeof latencyMs === 'number' && (
                    <span className="text-[11px] font-mono opacity-70">
                        {latencyMs} ms
                    </span>
                )}
                <StatusIcon
                    size={16}
                    className={ok === null ? 'animate-spin' : ''}
                />
            </div>
        </div>
    );
};

/**
 * Live traffic-light system health dashboard for the admin Settings page.
 *
 * Polls `/api/health/deep` on demand (default) or on a 30 s auto-refresh
 * interval. Renders four probe pills:
 *   1. MongoDB — from the `shallow.db_ok` field.
 *   2. R2 write — from `r2_write.ok` + latency.
 *   3. Resend  — from `resend.ok` + HTTP status.
 *   4. Mongo counts — success + per-collection doc counts.
 *
 * All rendering is derived from the API payload; if the endpoint 500s the
 * card degrades gracefully to a red "cannot reach diagnostic endpoint"
 * banner. Auto-refresh is opt-in so admins don't burn Resend/R2 quotas on
 * an idle open tab.
 */
const SystemHealthCard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const intervalRef = useRef(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await healthAPI.deep();
            setData(res.data);
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || 'Failed to reach /api/health/deep');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!autoRefresh) return undefined;
        intervalRef.current = setInterval(refresh, 30_000);
        return () => clearInterval(intervalRef.current);
    }, [autoRefresh, refresh]);

    const mongoCounts = data?.mongo?.counts || {};
    const mongoCountEntries = Object.entries(mongoCounts);

    return (
        <Card data-testid="system-health-card" className="border-[#EFE5E1]">
            <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-3">
                    <CardTitle className="flex items-center gap-2 text-[#1A1A1A]">
                        <Activity size={18} className="text-[#C08478]" />
                        System Health
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                data-testid="system-health-autorefresh"
                                className="accent-[#C08478]"
                            />
                            Auto-refresh (30 s)
                        </label>
                        <Button
                            onClick={refresh}
                            disabled={loading}
                            variant="outline"
                            size="sm"
                            data-testid="system-health-refresh-btn"
                            className="border-[#EFE5E1] hover:bg-[#FBF8F6]"
                        >
                            {loading
                                ? <Loader2 size={13} className="animate-spin mr-1.5" />
                                : <RefreshCw size={13} className="mr-1.5" />}
                            Refresh
                        </Button>
                    </div>
                </div>
                {data && (
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-gray-500 font-mono">
                        <Badge variant="outline" className="text-[10px]" data-testid="system-health-mode">
                            {data.mode}
                        </Badge>
                        <span>{data.version === 'dev' ? 'dev build' : `v${data.version}`}</span>
                        <span>·</span>
                        <span>uptime {data.uptime_s}s</span>
                        <span>·</span>
                        <span>{new Date(data.timestamp).toLocaleTimeString()}</span>
                    </div>
                )}
            </CardHeader>
            <CardContent className="space-y-3">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700" data-testid="system-health-error">
                        <div className="font-semibold flex items-center gap-2">
                            <XCircle size={14} /> Diagnostic endpoint unreachable
                        </div>
                        <div className="mt-1 text-xs font-mono opacity-80">{error}</div>
                    </div>
                )}

                {!error && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Pill
                            icon={Database}
                            label="MongoDB"
                            ok={data ? data.shallow?.db_ok : null}
                            extra={data ? (data.mongo?.ok ? `${mongoCountEntries.length} collections` : data.mongo?.reason) : null}
                            latencyMs={data?.mongo?.elapsed_ms}
                            testid="system-health-pill-mongo"
                        />
                        <Pill
                            icon={HardDrive}
                            label="Cloudflare R2 (write)"
                            ok={data ? data.r2_write?.ok : null}
                            extra={data?.r2_write?.path || data?.r2_write?.reason}
                            latencyMs={data?.r2_write?.elapsed_ms}
                            testid="system-health-pill-r2"
                        />
                        <Pill
                            icon={Mail}
                            label="Resend (email)"
                            ok={data ? data.resend?.ok : null}
                            extra={data?.resend
                                ? (data.resend.status ? `HTTP ${data.resend.status}` : data.resend.reason)
                                : null}
                            latencyMs={data?.resend?.elapsed_ms}
                            testid="system-health-pill-resend"
                        />
                        <Pill
                            icon={Zap}
                            label="R2 configured"
                            ok={data ? data.shallow?.r2_configured : null}
                            extra={data?.shallow?.r2_configured ? 'boto3 session up' : 'local fallback'}
                            testid="system-health-pill-r2-config"
                        />
                    </div>
                )}

                {mongoCountEntries.length > 0 && (
                    <details className="pt-2" data-testid="system-health-collections">
                        <summary className="text-xs text-gray-500 cursor-pointer select-none hover:text-[#1A1A1A]">
                            Collection counts ({mongoCountEntries.length})
                        </summary>
                        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {mongoCountEntries.map(([name, count]) => (
                                <div
                                    key={name}
                                    className="flex items-center justify-between bg-[#FBF8F6] border border-[#EFE5E1] rounded px-2.5 py-1.5"
                                    data-testid={`system-health-count-${name}`}
                                >
                                    <span className="text-xs text-gray-600">{name}</span>
                                    <span className="text-xs font-mono font-semibold text-[#1A1A1A]">
                                        {count.toLocaleString()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </details>
                )}
            </CardContent>
        </Card>
    );
};

export default SystemHealthCard;
